// Behavioral Records — persistent archive layer over rehearsal_sessions.
// Derives quiet, observational metadata for each completed session so the
// archive can render archival cards without re-running heavy analysis.

import { supabase } from "@/integrations/supabase/client";
import { detectArchetypes, ARCHETYPES, type ArchetypeKey } from "@/lib/archetypes";
import { detectResonantMoments } from "@/lib/resonantMoments";

export interface ArchivedSession {
  id: string;
  createdAt: string;
  completedAt: string | null;
  scenarioLabel: string;          // "Relationship Scenario" / "Workplace Scenario"
  archetypeName: string;          // "Gaslighting Archetype" or "The Deflector"
  archetypeKey: ArchetypeKey | null;
  observation: string;            // single-line behavioral observation
  resonantPreview: string | null; // short quoted line
  recurring: boolean;             // archetype has appeared before
}

interface RawSession {
  id: string;
  created_at: string;
  completed_at: string | null;
  entry_type: string | null;
  messages: any;
  tactics_sequence: any;
  forensic_baseline: any;
}

const wc = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

const scenarioLabelFromEntry = (entry: string | null, baseline: any): string => {
  // Try forensic baseline patterns for richer label
  const patterns: any[] = baseline?.patterns || [];
  const top = patterns?.[0]?.name;
  if (top) {
    const clean = String(top).replace(/_/g, " ");
    return /scenario$/i.test(clean) ? clean : `${clean} Scenario`;
  }
  if (entry === "professional") return "Workplace Scenario";
  if (entry === "personal") return "Personal Scenario";
  return "Behavioral Scenario";
};

const observationFor = (msgs: any[], tactics: any[]): string => {
  const userMsgs = msgs.filter((m) => m?.role === "user");
  const lengths = userMsgs.map((m) => wc(m?.content || ""));
  if (!lengths.length) return "Session recorded.";

  const half = Math.floor(userMsgs.length / 2) || 1;
  const earlyAvg = lengths.slice(0, half).reduce((a, b) => a + b, 0) / half;
  const lateAvg = lengths.slice(half).reduce((a, b) => a + b, 0) / Math.max(1, lengths.length - half);

  const seq: string[] = (tactics || []).map((t: any) => String(t?.tactic || "").toLowerCase());
  const lateHas = (kw: string) => seq.slice(Math.floor(seq.length / 2)).some((s) => s.includes(kw));

  if (lateHas("darvo") || lateHas("reversal")) return "Boundary softening observed after emotional reversal.";
  if (lateHas("guilt") || lateHas("shame")) return "Emotional explanation increased under pressure.";
  if (lateHas("invalidat") || lateHas("dismiss")) return "Responses tightened after repeated invalidation.";
  if (lateAvg > earlyAvg * 1.4) return "Engagement intensified after invalidation.";
  if (lateAvg < earlyAvg * 0.7) return "Composure held as pressure escalated.";
  return "Behavioral exchange recorded under pressure.";
};

export async function fetchArchivedSessions(
  userId: string,
  limit = 30
): Promise<ArchivedSession[]> {
  const { data, error } = await supabase
    .from("rehearsal_sessions")
    .select("id, created_at, completed_at, entry_type, messages, tactics_sequence, forensic_baseline")
    .eq("user_id", userId)
    .eq("status", "completed")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error || !data) return [];

  const archetypeCounts = new Map<ArchetypeKey, number>();
  const sessions: ArchivedSession[] = [];

  // First pass: build archetype frequency
  const detectedPerSession: { row: RawSession; topKey: ArchetypeKey | null }[] = [];
  for (const row of data as RawSession[]) {
    const msgs = (row.messages as any[]) || [];
    const seq = (row.tactics_sequence as any[]) || null;
    const detected = detectArchetypes(msgs, seq);
    const topKey = detected[0]?.archetype.key ?? null;
    if (topKey) archetypeCounts.set(topKey, (archetypeCounts.get(topKey) || 0) + 1);
    detectedPerSession.push({ row, topKey });
  }

  // Second pass: build cards (newest first, so "recurring" only marks subsequent)
  const seenSoFar = new Set<ArchetypeKey>();
  // Walk oldest → newest to mark recurring on later occurrences
  for (let i = detectedPerSession.length - 1; i >= 0; i--) {
    const { row, topKey } = detectedPerSession[i];
    const msgs = (row.messages as any[]) || [];
    const tactics = (row.tactics_sequence as any[]) || [];
    const moments = detectResonantMoments(msgs as any);
    const resonantPreview = moments[0]?.quote
      ? `"${String(moments[0].quote).slice(0, 90)}${moments[0].quote.length > 90 ? "…" : ""}"`
      : null;

    const recurring = topKey ? seenSoFar.has(topKey) : false;
    if (topKey) seenSoFar.add(topKey);

    sessions.push({
      id: row.id,
      createdAt: row.created_at,
      completedAt: row.completed_at,
      scenarioLabel: scenarioLabelFromEntry(row.entry_type, row.forensic_baseline),
      archetypeName: topKey ? ARCHETYPES[topKey].name : "Behavioral Pattern",
      archetypeKey: topKey,
      observation: observationFor(msgs, tactics),
      resonantPreview,
      recurring,
    });
  }

  // Reverse back to newest-first
  return sessions.reverse();
}

export function formatArchiveTimestamp(iso: string): string {
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date} • ${time}`;
}
